import { combineConfigs, noLegendConfig } from "@similarweb/pro-logic-components";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";
import dayjs from "dayjs";
import { translate } from "app/filters/i18nFilter";
import type { FC } from "react";
import { renderToString } from "react-dom/server";
import styled from "styled-components";
import { abbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import {
    xAxisFormatter,
    yAxisFormatter,
} from "app/pages/keyword-research/keyword-analysis/pages/keywords-overview/trend-over-time/trendOverTimeUtils";
import { getIntervalConfig } from "app/pages/website-analysis/traffic-sources/display-ads/overview/graph/helpers/DisplayAdsGraphDataParsers";
import { getTooltipHeader } from "app/UtilitiesAndConstants/UtilitiesComponents/chartTooltipHeaderWithMTDSupport";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import type { IDurationData } from "app/services/DurationService";
import { BasicDurations } from "app/services/DurationService";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import ChangeBellWithoutTooltip from "pro-features/components/Chart/src/configs/plotLines/ChangeBellWithoutTooltip";
import type { IPoint } from "app/pages/keyword-analysis/utils/AnnotationBanner";
import { displayAnnotation, getAnnotationDateForGranularity } from "app/pages/keyword-analysis/utils/AnnotationBanner";
import { swSettings } from "app/common/services/swSettings";
import { MetricNames } from "app/common/keyword-research/keywords-overview/trendOverTimeTypes";

const TooltipWrapper = styled.div`
    height: fit-content;
    .trend-over-time-tooltip > :first-child {
        ${setFontVariant("subtitle1", colorsPalettes.carbon[500])};
    }
`;

const ShareWrapper = styled.div`
    > div {
        color: ${colorsPalettes.carbon[300]};
    }
`;

const getTooltipHeaderMetricTitle = () => translate("keyword.analysis.overview.page.v2.trend_over_time.tooltip.metric");

const getTooltipHeaderValue = () => translate("keyword.analysis.overview.page.v2.trend_over_time.tooltip.value");

interface TooltipRow {
    metric: { label: string; color: string };
    value: string;
    share: string;
}

const getColumnDefinition = (showSourceTypeBreakdown: boolean): Array<ColumnDefinition<TooltipRow> & { key: string }> =>
    [
        {
            key: getTooltipHeaderMetricTitle(),
            title: getTooltipHeaderMetricTitle(),
            selector: "metric" as const,
            align: "left",
            size: ChartTooltip.LabelCellDefaultWidth,
            render: ({ metric }) => (
                <ChartTooltip.LabelCell
                    label={metric.label}
                    backgroundColor={metric.color}
                />
            ),
        },
        showSourceTypeBreakdown && {
            key: getTooltipHeaderValue(),
            title: "",
            selector: "share" as const,
            align: "right",
            size: ChartTooltip.ValueCellDefaultwidth,
            render: ({ share }) =>
                !share ? null : (
                    <ShareWrapper>
                        <ChartTooltip.ValueCell value={share} />
                    </ShareWrapper>
                ),
        },
        {
            key: getTooltipHeaderValue(),
            title: getTooltipHeaderValue(),
            selector: "value" as const,
            align: "right",
            size: ChartTooltip.ValueCellDefaultwidth,
            render: ({ value }) => <ChartTooltip.ValueCell value={value} />,
        },
    ].filter(Boolean) as Array<ColumnDefinition<TooltipRow> & { key: string }>;
interface ITrendOverTimeGraphTooltipProps {
    points: IPoint[];
    date: number | string;
    graphGranularity: GranularityNames;
    lastSupportedDate: number;
    dateForAnnotationDay: string;
    showSourceTypeBreakdown: boolean;
}

const TrendOverTimeGraphTooltip: FC<ITrendOverTimeGraphTooltipProps> = ({
    points,
    date,
    graphGranularity,
    lastSupportedDate,
    dateForAnnotationDay,
    showSourceTypeBreakdown,
}) => {
    const totalShare = points?.reduce((acc, point) => {
        if (point.name === MetricNames.VOLUME) {
            return acc;
        }
        return acc + point.value;
    }, 0);
    const data = points?.map((point) => {
        const shouldDisplayShare = showSourceTypeBreakdown && point.name !== MetricNames.VOLUME;
        return {
            metric: {
                label: point.name,
                color: point.color,
            },
            value: abbrNumberFilter()(point.value),
            ...(shouldDisplayShare && {
                share: percentageSignFilter()(point.value / totalShare, 2),
            }),
        };
    });
    const tooltipTitle = getTooltipHeader(graphGranularity, [{ x: date }], lastSupportedDate as any, false);

    return (
        <TooltipWrapper>
            <ChartTooltip
                width={270}
                title={tooltipTitle}
                columnDefinition={getColumnDefinition(showSourceTypeBreakdown)}
                data={data}
                className="trend-over-time-tooltip"
            >
                {displayAnnotation(
                    points,
                    translate("keyword.research.improved.algorithms.annotation.text"),
                    getAnnotationDateForGranularity(dateForAnnotationDay, graphGranularity),
                )}
            </ChartTooltip>
        </TooltipWrapper>
    );
};

export const getTrendOverTimeGraphConfig = (
    is28Days: boolean,
    graphDuration: string,
    graphGranularity: GranularityNames,
    durationObject: IDurationData,
    showSourceTypeBreakdown: boolean,
) => {
    const dateForAnnotationDay = swSettings.current?.resources?.VersionRerurnDate__1d6d228;
    return combineConfigs({}, [
        noLegendConfig,
        getIntervalConfig(graphGranularity)(!showSourceTypeBreakdown ? { type: chartTypes.LINE } : { type: undefined }),
        ChangeBellWithoutTooltip(dayjs(getAnnotationDateForGranularity(dateForAnnotationDay, graphGranularity)), true),
        {
            chart: {
                height: 264,
                marginRight: 24,
                spacing: [0, 0, 0, 0],
            },
            tooltip: {
                formatter() {
                    const points = this.points?.map((point) => {
                        return {
                            color: point.color as string,
                            name: point.series.name,
                            value: point.y,
                            date: point.x as any,
                        };
                    });

                    return renderToString(
                        <TrendOverTimeGraphTooltip
                            points={points}
                            date={this.x}
                            graphGranularity={graphGranularity}
                            lastSupportedDate={durationObject.raw.to.valueOf()}
                            dateForAnnotationDay={dateForAnnotationDay}
                            showSourceTypeBreakdown={showSourceTypeBreakdown}
                        />,
                    );
                },
            },
            yAxis: {
                showFirstLabel: true,
                startOnTick: true,
                minPadding: 0,
                labels: {
                    formatter: yAxisFormatter,
                },
            },
            xAxis: {
                minPadding: 0,
                maxPadding: 0,
                labels: {
                    formatter: xAxisFormatter,
                },
                plotLines: [
                    is28Days &&
                    graphDuration !== BasicDurations.LAST_TWENTY_EIGHT_DAYS &&
                    graphGranularity !== GranularityNames.MONTHLY
                        ? {
                              color: colorsPalettes.carbon[200],
                              width: 1,
                              dashStyle: "ShortDash",
                              value: dayjs.utc().subtract(28, "days").valueOf(),
                              zIndex: 100,
                              label: {
                                  text: translate("keywordanalysis.organic2.clicks.over.time.graph.plotLine.last28"),
                                  align: "left",
                                  y: 20,
                                  rotation: 0,
                                  style: { color: colorsPalettes.carbon[200] },
                              },
                          }
                        : {},
                ],
                tickPositioner: function () {
                    const allXValues: number[] = this.chart.series.flatMap((series) =>
                        (series.data?.map((point) => point.x) ?? []).filter((x): x is number => Number.isFinite(x)),
                    );

                    if (allXValues.length === 0) {
                        return undefined;
                    }
                    const uniqueXValues = [...new Set(allXValues)].sort((a, b) => a - b);

                    if (uniqueXValues.length <= 6) {
                        return undefined;
                    }

                    if (uniqueXValues.length > 50) {
                        return uniqueXValues.filter((_, i) => i % 10 === 0);
                    } else if (uniqueXValues.length > 8) {
                        return uniqueXValues.filter((_, i) => i % 3 === 0);
                    }

                    return uniqueXValues.filter((_, i) => i % 2 === 0);
                },
            },
        },
    ]);
};
